import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton, Kart, Uyari } from "@kantin/ui";
import { apiDosyaIndir, apiOku } from "./api.js";
import { IslemDurumu, Sayfa, SayfaBasligi, SorguDurumu } from "./ortak.js";

interface AktarimOzeti {
  kisi_sayisi: number;
  ogrenci_sayisi: number;
  sifresiz_sayisi: number;
  kimliksiz_sayisi: number;
  pasif_ogrenci_sayisi: number;
}

export function VeliGirisBilgileri({ okulId }: { okulId: string }) {
  const istemci = useQueryClient();
  const ozet = useQuery({
    queryKey: ["veli-giris-bilgileri", okulId],
    queryFn: () => apiOku<AktarimOzeti>("/veli-giris-bilgileri"),
  });
  const indir = useMutation({
    mutationFn: () => apiDosyaIndir(
      "/veli-giris-bilgileri/excel",
      `veli-giris-bilgileri-${okulId}.xlsx`,
      undefined,
      "POST",
    ),
    onSettled: () => {
      void istemci.invalidateQueries({ queryKey: ["veli-giris-bilgileri", okulId] });
      void istemci.invalidateQueries({ queryKey: ["kisi"] });
    },
  });

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Veli giriş bilgileri"
        aciklama="Kişiler listesini ve öğrencilerin veli giriş bilgilerini tek Excel dosyasında indirin."
      />
      <SorguDurumu
        yukleniyor={ozet.isLoading}
        hata={ozet.error}
        bos={ozet.data?.kisi_sayisi === 0}
        bosBaslik="Henüz kişi kaydı yok"
        bosAciklama="Okula kişi eklendiğinde Excel dosyasını buradan indirebilirsiniz."
        yenile={() => void ozet.refetch()}
      >
        {ozet.data && (
          <Kart baslik="Excel dışa aktarımı">
            <div className="flex flex-col gap-5 p-4">
              <dl className="m-0 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                  ["Toplam kişi", ozet.data.kisi_sayisi],
                  ["Öğrenci", ozet.data.ogrenci_sayisi],
                  ["Şifre atanacak", ozet.data.sifresiz_sayisi],
                  ["Kimlik no eksik", ozet.data.kimliksiz_sayisi],
                ].map(([etiket, deger]) => (
                  <div key={etiket}>
                    <dt className="text-sm text-murekkep-3">{etiket}</dt>
                    <dd className="m-0 mt-1 text-2xl font-semibold text-murekkep">{deger}</dd>
                  </div>
                ))}
              </dl>
              <div className="space-y-2 text-sm text-murekkep-2">
                <p className="m-0">
                  İndirirken şifresi boş olan tüm öğrencilere iki kelime ve iki rakamdan
                  oluşan şifre atanır ve kaydedilir. Mevcut şifreler korunur.
                </p>
                <p className="m-0">
                  “Veli giriş bilgileri” sekmesi öğrenci, veli adı, TCKN ve şifreyi;
                  “Kişiler” sekmesi öğrenci, personel ve misafir listesini içerir.
                  Aktif ve pasif kayıtların tamamı dosyaya alınır.
                </p>
              </div>
              {(ozet.data.kimliksiz_sayisi > 0 || ozet.data.pasif_ogrenci_sayisi > 0) && (
                <Uyari tur="bilgi" baslik="Giriş bilgisi eksik kayıtlar">
                  Kimlik numarası eksik {ozet.data.kimliksiz_sayisi} öğrenci ve
                  pasif {ozet.data.pasif_ogrenci_sayisi} öğrenci var. Şifreleri hazırlanır;
                  giriş için kimlik numarası ve aktif kayıt gerekir. Bu durumlar Excel’de belirtilir.
                </Uyari>
              )}
              <div>
                <Buton tur="birincil" yukleniyor={indir.isPending} onClick={() => indir.mutate()}>
                  Excel indir
                </Buton>
              </div>
            </div>
          </Kart>
        )}
      </SorguDurumu>
      <IslemDurumu
        hata={indir.error}
        basari={indir.isSuccess ? "Eksik şifreler kaydedildi. Excel indirmesi başlatıldı." : null}
      />
    </Sayfa>
  );
}
